import { forwardRef, type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon24ActionGo } from '@figma/fpl-icons';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { themeColor } from '@/UI/themeColors';
import { I18N_KEYS } from '@/language/keys';
import { ArrowIcon } from '@/UI/Icons/ArrowIcon';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { useWithLoader } from '@/hooks/useWithLoader';

const BUTTON_STYLES = {
  minWidth: '120px',
  width: 'fit-content',
  fontWeight: 500,
  textTransform: 'none',
  boxShadow: 'none',
  position: 'relative',
  textWrap: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  fontSize: '0.75rem',
  height: '24px',
  borderRadius: '4px',
  px: 1,
  py: 0.5,
  backgroundColor: themeColor.white100Secondary,
  '&:hover': { backgroundColor: themeColor.white40 },
} as const;

export const RunSelectedButton = forwardRef<
  HTMLButtonElement,
  ComponentProps<typeof ButtonContained> & { onClick: () => Promise<void> }
>(function RunSelectedButton({ sx, onClick, disabled, ...props }, ref) {
  const { t } = useTranslation();
  const isFplMigrationEnabled = useFplMigrationFlag();

  const { isLoading, execute } = useWithLoader(onClick, { sync: true });
  return (
    <ButtonContained
      ref={ref}
      startIcon={
        isFplMigrationEnabled ? (
          <Icon24ActionGo />
        ) : (
          <ArrowIcon style={{ transform: 'rotate(90deg)', width: 16, height: 16 }} strokeWidth="1.125" />
        )
      }
      fullWidth={isFplMigrationEnabled === true}
      mode="filled-light"
      size={isFplMigrationEnabled === true ? 'xs' : undefined}
      {...props}
      disabled={isLoading || disabled}
      onClick={execute}
      sx={{
        ...BUTTON_STYLES,
        ...(sx || {}),
      }}
    >
      {t(I18N_KEYS.RECIPE_MAIN.FLOW.DRAWER.RUN_MODELS_SECTION.CTA)}
    </ButtonContained>
  );
});
